/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminSystemPage.tsx
 * ============================================================
 */

import { Cpu, Clock, Database, ListChecks, Activity } from 'lucide-react';
import { useAdminSystem } from '@/hooks/useAdmin';

export default function AdminSystemPage() {
  const { data: info, isLoading } = useAdminSystem();

  if (isLoading || !info) return <div className="text-slate-400 text-sm">Loading system info…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">System</h1>
        <p className="text-xs text-slate-400 mt-0.5">Backend runtime + database stats + scheduled tasks.</p>
      </div>

      {/* Runtime */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Runtime</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={Cpu}      label="Node version"     value={info.nodeVersion} />
          <Card icon={Activity} label="Memory (RSS)"     value={`${info.processMemoryMB} MB`} />
          <Card icon={Clock}    label="Uptime"           value={formatUptime(info.uptimeSeconds)} />
          <Card icon={Clock}    label="Started at"       value={new Date(info.startedAt).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })} small />
        </div>
      </section>

      {/* Scheduler */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
          <ListChecks className="w-3.5 h-3.5" /> Scheduled tasks
        </h2>
        <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
          {info.schedulerTasks.map((t) => (
            <div key={t.name} className="p-3 flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
              <div className="flex-1 font-mono text-xs text-slate-300">{t.name}</div>
              <div className="text-[11px] text-slate-500">running</div>
            </div>
          ))}
        </div>
      </section>

      {/* DB stats */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
          <Database className="w-3.5 h-3.5" /> Database tables
        </h2>
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-800">
                <th className="p-3 font-medium">Table</th>
                <th className="p-3 font-medium text-right">Rows</th>
                <th className="p-3 font-medium text-right">Size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {info.dbStats.map((row) => (
                <tr key={row.table}>
                  <td className="p-3 font-mono text-slate-300">{row.table}</td>
                  <td className="p-3 text-right tabular-nums">{row.rows.toLocaleString()}</td>
                  <td className="p-3 text-right text-slate-400">{row.bytes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Card({ icon: Icon, label, value, small }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; small?: boolean }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
      <div className="h-8 w-8 rounded-md bg-slate-800 text-slate-300 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className={`mt-3 font-bold truncate ${small ? 'text-sm' : 'text-lg'}`}>{value}</div>
      <div className="text-[11px] text-slate-400 mt-0.5 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
